import { trackByFactory } from '@mode/capra';
import { Component, ChangeDetectionStrategy, Input, EventEmitter, Output, TemplateRef } from '@angular/core';
import { Attr } from '@mode-switch/model';
import { EncField, EncFieldGroup, VariableType, BasicChannel } from '@mode-vegas/vegas';
import { DragFieldInfo } from '@mode/charts/contract';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';

@Component({
  selector: 'mode-encoding-dropzone',
  templateUrl: './encoding-dropzone.component.html',
  styleUrls: ['./encoding-dropzone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EncodingDropzoneComponent {
  @Input() header!: string;
  @Input() channel!: BasicChannel;
  @Input() isDraggable!: boolean;
  @Input() placeholderClasses = true;
  @Input() fields!: Array<EncField | EncFieldGroup>;
  @Input() viewSource!: EncField[];
  @Input() view!: FlamingoTypes.ModeView;
  @Input() template!: TemplateRef<any>;
  @Input() dragField!: DragFieldInfo;
  @Input() allowDiscrete!: boolean;
  @Input() allowContinuous!: boolean;
  @Input() allowScalars!: boolean;
  @Input() chartType!: ChartTypes.ChartType;
  @Input() isMetrics!: boolean;
  @Input() namesChannel: BasicChannel = 'row';
  @Input() isChartEncodingComplete = false;
  @Input() typeAheadShouldReplace = false;
  @Input() showTypeAhead = true;

  @Output() dropField = new EventEmitter();
  @Output() dragStart = new EventEmitter();
  @Output() dragEnd = new EventEmitter();
  @Output() fieldDropOnField = new EventEmitter();
  @Output() switchNamesAxes = new EventEmitter();

  // tooltipSettings
  readonly tooltipNameRowContent = 'Show as Row';
  readonly tooltipNameColumnContent = 'Show as Column';
  readonly tooltipInteractive = false;
  readonly tooltipAppendTo: Element | undefined;
  readonly tooltipDelay = 500;

  trackById = trackByFactory<EncField>(({ id }) => id);

  constructor(private analyticsService: AnalyticsService) {}

  get isDisallowedField(): boolean {
    const dragField: DragFieldInfo | undefined = this.dragField;
    if (!dragField) {
      return false;
    }

    if (dragField.formula?.isSystem && this.chartType === 'vegasPivotTable') {
      return true;
    }

    if (dragField.formula?.isSystem || dragField.groupId != null) {
      return this.channel === 'filter' || this.channel === 'value';
    }

    return false;
  }

  get isDragFieldContinuous(): boolean {
    if (this.dragField?.formula != null) {
      return this.dragField.formula.varType === Attr.Type.Continuous;
    } else {
      return false;
    }
  }

  get isDragFieldGroup(): boolean {
    return this.dragField?.groupId != null;
  }

  get measureNamesInRow() {
    return this.namesChannel === 'row';
  }

  get placeholderText() {
    if (this.typeAheadShouldReplace) {
      return this.isEmpty() ? 'Add a field here...' : 'Replace field...';
    } else {
      return 'Add fields here...';
    }
  }

  onDropItem($event: any) {
    const { attr } = $event;
    this.dropField.emit({ event: $event, channel: this.channel, attr });
    this.analyticsService.track(AnalyticsEvent.Name.DragEnd, {
      location: AnalyticsEvent.SubContext.Chart,
      action: 'Field dropped',
      resource: AnalyticsEvent.Context.Visualization,
    });
  }

  onDragStart($event: any, field: EncField | EncFieldGroup) {
    this.dragStart.emit({ event: $event, field, source: this.channel });
    this.analyticsService.track(AnalyticsEvent.Name.DragStart, {
      location: AnalyticsEvent.SubContext.Chart,
      action: 'Field drag started',
      resource: AnalyticsEvent.Context.Visualization,
    });
  }

  onDragEnd($event: any, field: EncField | EncFieldGroup) {
    this.dragEnd.emit({ event: $event, field });
  }

  onFieldDrop($event: any, field: EncField) {
    this.fieldDropOnField.emit({ event: $event, field, channel: this.channel });
    this.analyticsService.track(AnalyticsEvent.Name.DragEnd, {
      location: AnalyticsEvent.SubContext.Chart,
      action: 'Field replaced',
      resource: AnalyticsEvent.Context.Visualization,
    });
  }

  onAttrSelected($event: any) {
    const { event, attr, onFinalize } = $event;
    event.index = this.fields.length + 1;
    this.dropField.emit({ event, channel: this.channel, attr, onFinalize });
  }

  isDiscrete(field: EncField | EncFieldGroup) {
    return this.isEncField(field) && (field as EncField).variable.type === VariableType.DISCRETE;
  }

  isContinuous(field: EncField | EncFieldGroup) {
    return this.isEncFieldGroup(field) || (field as EncField).variable.type === VariableType.CONTINUOUS;
  }

  isDragEqualToFieldType(field: EncField) {
    if (this.dragField && this.dragField.formula) {
      return (
        (this.dragField.formula.isDiscrete && this.isDiscrete(field)) ||
        (this.dragField.formula.isContinuous && this.isContinuous(field))
      );
    } else {
      return false;
    }
  }

  isEncField(field: EncField | EncFieldGroup) {
    return 'formula' in field;
  }

  isEncFieldGroup(field: EncField | EncFieldGroup) {
    return 'type' in field;
  }

  isEmpty() {
    return (this.fields?.length ?? 0) < 1;
  }

  isDisplayTypeahead() {
    return !this.isMetrics && this.showTypeAhead;
  }

  onSwitchNamesAxes() {
    this.switchNamesAxes.emit();
  }
}
